import { Trip } from "../../../../../state/trips"
import { ReportProps } from ".."
import { groupBy } from "../../../../../utils/groupBy"
import { getMileageRate, Rate } from "../../../../../utils/mileageRate"
import { paginate, sum } from "../../../../../utils/array"
import { RATES } from "@/constants/rates"
import { PlainDate } from "@/utils/date"

export const generatePDFReport = (
	rates: readonly Rate[],
	dateRange: [PlainDate, PlainDate],
	trips: Trip[],
	vehicleModel: string
): ReportProps => {
	const sortedTrips = trips.sort((a, b) => PlainDate.compare(a.date, b.date))

	const groupedTrips = groupBy(sortedTrips, "vehicle")
	const pageSize = 8
	const vehicles = Object.entries(groupedTrips).map(([vehicle, trips]) => ({
		vehicle: vehicleModel,
		business: sum(trips, (x) => x.mileage),
		commute: 0,
		personal: 0,
		totalDistance: sum(trips, (x) => x.mileage) + 0 + 0, // count commute and personal
		businessValue: sum(trips, (x) => x.mileage * getMileageRate(PlainDate.from(x.date), rates)),
		totalBusinessValue: sum(trips, (x) => x.mileage * getMileageRate(PlainDate.from(x.date), rates)),
		odometer: trips[0]?.odometer,
		parking: 0,
		tolls: 0,
		medical: 0,
		moving: 0,
		charity: 0,
	}))

	const businessRateRange = calcBusinessRate(sortedTrips)

	return {
		businessRateRange: businessRateRange,
		dateRange: [dateRange[0], dateRange[1]],
		summary: {
			totalDistance: sum(sortedTrips, (x) => x.mileage),
			totalDrives: sortedTrips.length,
			totalValue: sum(
				sortedTrips,
				(x) => x.mileage * getMileageRate(PlainDate.from(x.date), rates)
			),
			businessValue: sum(
				sortedTrips,
				(x) => x.mileage * getMileageRate(PlainDate.from(x.date), rates)
			),
			personalValue: 0,
			parking: 0,
			tolls: 0,
		},
		vehicles: vehicles,
		vehiclesTotal: {
			vehicle: vehicleModel,
			business: sum(vehicles, (x) => x.business),
			commute: 0,
			personal: 0,
			totalDistance: sum(vehicles, (x) => x.totalDistance),
			businessValue: sum(vehicles, (x) => x.businessValue),
			parking: 0,
			tolls: 0,
			totalBusinessValue: sum(vehicles, (x) => x.totalBusinessValue),
			medical: 0,
			moving: 0,
			charity: 0,
			odometer: sum(vehicles, (x) => x.odometer || 0),
		},
		trips: Object.entries(groupedTrips).map(([vehicle, trips]) => ({
			vehicle: vehicleModel,
			pages: paginate(trips, pageSize).map((page) => ({
				trips: page.map((trip) => {
					const value = trip.mileage * getMileageRate(PlainDate.from(trip.date), rates)
					return {
						date: PlainDate.from(trip.date),
						timeStart: trip.timeStart,
						why: "Business",
						start: trip.startAddress,
						end: trip.endAddress,
						notes: "-",
						rate: getMileageRate(PlainDate.from(trip.date), rates),
						distance: trip.mileage,
						value: value,
						parking: 0,
						tolls: 0,
						total: value,
					}
				}),
				total: {
					distance: sum(page, (x) => x.mileage),
					value: sum(page, (x) => x.mileage * getMileageRate(PlainDate.from(x.date), rates)),
					parking: 0,
					tolls: 0,
					total: sum(page, (x) => x.mileage * getMileageRate(PlainDate.from(x.date), rates)),
				},
			})),
		})),
		tripsTotal: {
			distance: sum(sortedTrips, (x) => x.mileage),
			value: sum(sortedTrips, (x) => x.mileage * getMileageRate(PlainDate.from(x.date), rates)),
			parking: 0,
			tolls: 0,
			total: sum(sortedTrips, (x) => x.mileage * getMileageRate(PlainDate.from(x.date), rates)),
		},
	}
}

export const calcBusinessRate = (trips: Trip[]) => {
	const businessRates = new Set(trips.map((x) => getMileageRate(PlainDate.from(x.date), RATES)))
	const businessRatesArr = Array.from(businessRates).sort((a, b) => a - b)
	const rateLeft = businessRatesArr[0]!
	const rateRight = businessRatesArr[businessRatesArr.length - 1]!

	return businessRates.size > 1 ? [rateLeft, rateRight] : [rateLeft]
}
